import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServiceManagerService } from '../service-manager.service';
import { HttpMethod } from '../../constants/constant';
import { HttpCustomOptions } from 'src/services/http-interceptor.service';

interface KeyValuePair {
  key: string;
  value: string;
  section: string;
}

@Component({
  selector: 'app-language-detail',
  templateUrl: './language-detail.component.html',
  styleUrls: ['./language-detail.component.scss']
})
export class LanguageDetailComponent implements OnInit {
  isNew = false;
  contentId: number | null = null;
  pairs: KeyValuePair[] = [];
  sections: string[] = [];
  availableLanguages: any[] = [];
  selectedLanguageId: number | null = null;
  selectedStatus = 'active';
  isLoading = true;

  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      ['link'],
      ['clean'],
    ],
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private serviceManager: ServiceManagerService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    this.isNew = id === 'new';

    this.serviceManager.CallBackendServiceAPI('list-languages', {}).subscribe((data: any) => {
      this.availableLanguages = data.languages || [];

      if (this.isNew) {
        this.pairs = [];
        this.isLoading = false;
      } else {
        this.contentId = Number(id);
        this.serviceManager
          .CallBackendServiceAPI('get-language', { language_content_id: this.contentId })
          .subscribe((res: any) => {
            const lang = this.availableLanguages.find((l: any) => l.id === this.contentId);
            if (lang) {
              this.selectedLanguageId = lang.language_id;
              this.selectedStatus = lang.status;
            }
            this.pairs = this.flattenJson(res.data || {});
            this.sections = [...new Set(this.pairs.map(p => p.section))];
            // Group by section and sort each section so questionN/answerN are sequential
            const sorted: KeyValuePair[] = [];
            for (const section of this.sections) {
              sorted.push(...this.sortPairsInSection(this.pairs.filter(p => p.section === section)));
            }
            this.pairs = sorted;
            this.isLoading = false;
          });
      }
    });
  }

  flattenJson(obj: any, prefix = '', section = ''): KeyValuePair[] {
    const result: KeyValuePair[] = [];
    for (const key of Object.keys(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const currentSection = section || key;
      if (obj[key] !== null && typeof obj[key] === 'object') {
        result.push(...this.flattenJson(obj[key], fullKey, currentSection));
      } else {
        result.push({ key: fullKey, value: obj[key] ?? '', section: currentSection });
      }
    }
    return result;
  }

  private sortPairsInSection(pairs: KeyValuePair[]): KeyValuePair[] {
    return [...pairs].sort((a, b) => {
      const leafA = a.key.split('.').pop() || '';
      const leafB = b.key.split('.').pop() || '';
      const rawNumA = leafA.replace(/\D/g, '');
      const rawNumB = leafB.replace(/\D/g, '');
      const numA = rawNumA ? parseInt(rawNumA, 10) : 0;
      const numB = rawNumB ? parseInt(rawNumB, 10) : 0;
      if (numA !== numB) return numA - numB;
      // Same number suffix: question before answer, others (title etc.) first (no suffix → 0)
      const orderOf = (leaf: string) => {
        if (!rawNumA && !rawNumB) return 0; // no numeric suffix: keep original relative order
        if (leaf.startsWith('question')) return 0;
        if (leaf.startsWith('answer')) return 1;
        return -1;
      };
      return orderOf(leafA) - orderOf(leafB);
    });
  }

  unflattenJson(pairs: KeyValuePair[]): any {
    const result: any = {};
    for (const pair of pairs) {
      const parts = pair.key.split('.');
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = pair.value || null;
    }
    return result;
  }

  pairsForSection(section: string): KeyValuePair[] {
    return this.pairs.filter(p => p.section === section); // already sorted in ngOnInit
  }

  formatSectionLabel(section: string): string {
    const withSpaces = section
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
  }

  save(): void {
    const data = this.unflattenJson(this.pairs);
    const customOptions: HttpCustomOptions = {
      showErrorMessage: true,
      showSuccessMessage: true,
      createLoaderAction: true
    };

    if (this.isNew) {
      if (!this.selectedLanguageId) {
        alert('Please select a language.');
        return;
      }
      const body = { language_id: this.selectedLanguageId, data, status: this.selectedStatus };
      this.serviceManager
        .CallBackendServiceAPI('create-language', body, HttpMethod.POST, customOptions)
        .subscribe(() => this.router.navigate(['/language-content']));
    } else {
      const body = { id: this.contentId, data, status: this.selectedStatus };
      this.serviceManager
        .CallBackendServiceAPI('update-language', body, HttpMethod.POST, customOptions)
        .subscribe(() => this.router.navigate(['/language-content']));
    }
  }

  cancel(): void {
    this.router.navigate(['/language-content']);
  }

  trackByKey(_: number, pair: KeyValuePair): string {
    return pair.key;
  }

  isRichTextAnswer(pair: KeyValuePair): boolean {
    return pair.section === 'about' && /^answer\d+$/.test(pair.key.split('.').pop() || '');
  }
}
